import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, X, RefreshCw, Calendar, Zap, ChevronRight, Plus, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import ClientFormModal from '../components/ClientFormModal';

// ── Inline entity search ────────────────────────────────────────────
function EntitySearch({ label, hint, value, onChange }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const inputRef = useRef(null);

  const { data: results = [] } = useQuery({
    queryKey: ['entity-search', q],
    queryFn: () =>
      q.length > 0
        ? api.get('/clients', { params: { search: q } }).then(r => r.data)
        : Promise.resolve([]),
    enabled: q.length > 0,
  });

  const select = (item) => { onChange(item); setQ(''); setOpen(false); };
  const clear = () => { onChange(null); setQ(''); };

  // ClientFormModal refreshes the client caches itself, so every screen that
  // adds a client stays consistent. Here we only need to pick them up.
  const handleAdded = (client) => {
    setShowAdd(false);
    select(client);
  };

  if (value) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="flex items-center justify-between border-2 border-orange-400 rounded-lg px-3 py-2.5 bg-orange-50">
          <div>
            <p className="text-sm font-semibold text-gray-800">{value.name}</p>
            <p className="text-xs text-gray-500">{[value.phone, value.address].filter(Boolean).join(' · ')}</p>
          </div>
          <button type="button" onClick={clear} className="text-gray-400 hover:text-red-500 ml-2">
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {showAdd && <ClientFormModal title={`Add New ${label}`} onClose={() => setShowAdd(false)} onSaved={handleAdded} />}
      <div className="relative">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {label} <span className="text-gray-400 font-normal text-xs">{hint}</span>
          </label>
          <button type="button" onClick={() => setShowAdd(true)}
            className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600 font-medium">
            <Plus size={13} /> New
          </button>
        </div>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search by name or phone..."
            value={q}
            onChange={e => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            className="w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>
        {open && results.length > 0 && (
          <div className="absolute z-20 w-full bg-white border rounded-lg shadow-lg mt-1 max-h-52 overflow-y-auto">
            {results.map(item => (
              <button key={item.id} type="button" onMouseDown={() => select(item)}
                className="w-full text-left px-3 py-2.5 hover:bg-orange-50 border-b last:border-0">
                <p className="text-sm font-medium text-gray-800">{item.name}</p>
                <p className="text-xs text-gray-400">{[item.phone, item.address].filter(Boolean).join(' · ')}</p>
              </button>
            ))}
          </div>
        )}
        {open && q.length > 0 && results.length === 0 && (
          <div className="absolute z-20 w-full bg-white border rounded-lg shadow-lg mt-1 px-3 py-3 text-sm text-gray-500 flex items-center justify-between">
            <span>No results found</span>
            <button type="button" onMouseDown={() => { setOpen(false); setShowAdd(true); }}
              className="flex items-center gap-1 text-orange-500 hover:text-orange-600 font-medium text-xs">
              <Plus size={13} /> Add new
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ── Date quick-pick ─────────────────────────────────────────────────
function DatePicker({ value, onChange }) {
  const fmt = (d) => d.toISOString().split('T')[0];
  const today = new Date();

  const quick = [
    { label: 'ASAP', sublabel: 'Today', value: fmt(today) },
    {
      label: 'Next Day',
      sublabel: new Date(today.getTime() + 86400000).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' }),
      value: fmt(new Date(today.getTime() + 86400000)),
    },
    {
      label: '+2 Days',
      sublabel: new Date(today.getTime() + 2 * 86400000).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' }),
      value: fmt(new Date(today.getTime() + 2 * 86400000)),
    },
    {
      label: '+3 Days',
      sublabel: new Date(today.getTime() + 3 * 86400000).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' }),
      value: fmt(new Date(today.getTime() + 3 * 86400000)),
    },
  ];

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Scheduled Delivery Date</label>
      <div className="grid grid-cols-4 gap-2 mb-2">
        {quick.map(q => (
          <button
            key={q.value}
            type="button"
            onClick={() => onChange(q.value)}
            className={`flex flex-col items-center py-2 px-1 rounded-lg border text-xs transition-colors ${
              value === q.value
                ? 'bg-orange-500 border-orange-500 text-white'
                : 'border-gray-200 text-gray-600 hover:border-orange-400 hover:bg-orange-50'
            }`}
          >
            <span className="font-bold text-sm">{q.label}</span>
            <span className={`mt-0.5 ${value === q.value ? 'text-orange-100' : 'text-gray-400'}`}>{q.sublabel}</span>
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Calendar size={14} className="text-gray-400" />
        <input
          type="date"
          value={value}
          min={fmt(today)}
          onChange={e => onChange(e.target.value)}
          className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        />
        {value && (
          <button type="button" onClick={() => onChange('')} className="text-xs text-gray-400 hover:text-red-500">
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

// ── Extra boxes on the same pickup ──────────────────────────────────
// The sender hands over several boxes for ONE order. Each box gets its own
// pre-printed sticker so a stray one can be traced, but they stay a single
// consignment: one price, one invoice. Box 1 is the pickup's own number.
function NewParcelBoxes({ avail, mainNumber, chosen, setChosen }) {
  const [open, setOpen] = useState(false);
  const [holder, setHolder] = useState('');

  const main = String(mainNumber || '').toUpperCase();
  const numbers = (avail.holders.find(h => h.holder_key === holder)?.numbers || [])
    .filter(n => n.toUpperCase() !== main);

  const toggle = (n) => setChosen(c =>
    c.includes(n) ? c.filter(x => x !== n) : [...c, n]);

  const total = chosen.length + 1;

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
          Boxes {total > 1 && <span className="text-orange-600 normal-case">— {total} on this pickup</span>}
        </h2>
        {!open && (
          <button type="button" onClick={() => setOpen(true)}
            className="text-xs border border-gray-300 hover:border-orange-400 rounded-lg px-3 py-1.5 text-gray-700">
            {chosen.length ? 'Change boxes' : '+ More than one box?'}
          </button>
        )}
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {total > 1
          ? 'One price and one invoice for all of them. Each box is scanned separately, and the pickup is only done once every box has been scanned.'
          : 'Collecting several boxes for the same order? Add the stickers you put on the other boxes.'}
      </p>

      {chosen.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-3 py-1 font-mono">
            {main || 'auto'} · box 1
          </span>
          {chosen.map((n, i) => (
            <span key={n} className="text-xs bg-orange-50 border border-orange-200 text-orange-800 rounded-full px-3 py-1 font-mono flex items-center gap-1.5">
              {n} · box {i + 2}
              <button type="button" onClick={() => toggle(n)} className="text-orange-400 hover:text-red-500">✕</button>
            </span>
          ))}
        </div>
      )}

      {open && (
        avail.total === 0 ? (
          <div className="text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2.5">
            No unused issued barcodes. Issue some on the{' '}
            <a href="/barcodes" className="text-orange-500 hover:underline">Barcodes</a> page first.
          </div>
        ) : (
          <div className="border-2 border-orange-200 rounded-lg p-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Barcode book</label>
                <select value={holder} onChange={e => setHolder(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                  <option value="">— Choose holder —</option>
                  {avail.holders.map(h => (
                    <option key={h.holder_key} value={h.holder_key}>{h.holder_name} ({h.numbers.length})</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end text-xs text-gray-400">
                {chosen.length ? `${chosen.length} extra box${chosen.length === 1 ? '' : 'es'} selected` : 'Tick the other boxes below'}
              </div>
            </div>

            {holder && (
              <div className="border rounded-lg max-h-56 overflow-y-auto divide-y">
                {numbers.map(n => (
                  <label key={n} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-orange-50 cursor-pointer">
                    <input type="checkbox" checked={chosen.includes(n)}
                      onChange={() => toggle(n)} className="accent-orange-500" />
                    <span className="font-mono text-xs">{n}</span>
                  </label>
                ))}
                {numbers.length === 0 && (
                  <p className="px-3 py-3 text-xs text-gray-400">No unused barcodes left for this holder.</p>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <button type="button" onClick={() => setOpen(false)}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-semibold">
                Done — {total} box{total === 1 ? '' : 'es'}
              </button>
              {chosen.length > 0 && (
                <button type="button" onClick={() => setChosen([])}
                  className="border rounded-lg px-4 text-sm text-gray-600">Clear</button>
              )}
            </div>
          </div>
        )
      )}
    </div>
  );
}

// ── Main form ───────────────────────────────────────────────────────
export default function NewParcel() {
  const navigate = useNavigate();
  const [sender, setSender] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [trackingMode, setTrackingMode] = useState('auto'); // 'auto' | 'issued' | 'manual'
  const [issuedHolder, setIssuedHolder] = useState('');     // holder_key
  const [trackingInput, setTrackingInput] = useState('');
  const [trackingOk, setTrackingOk] = useState(null); // null | true | false
  const manualTracking = trackingMode !== 'auto';
  const [form, setForm] = useState({
    description: '', weight: '', length: '', width: '', height: '',
    price_tier_id: '', manual_price: '', notes: '', scheduled_delivery: '',
  });
  const [pickupDriverId, setPickupDriverId] = useState('');
  const [loading, setLoading] = useState(false);
  // Extra barcode stickers going on the other boxes of the same pickup
  const [extraBoxes, setExtraBoxes] = useState([]);

  const { data: tiers = [] } = useQuery({
    queryKey: ['price-tiers'],
    queryFn: () => api.get('/price-tiers').then(r => r.data),
  });

  // Pre-issued barcodes not yet used, grouped by who holds the book/roll
  const { data: availBarcodes = { holders: [], total: 0 } } = useQuery({
    queryKey: ['barcodes-available'],
    queryFn: () => api.get('/barcodes/available').then(r => r.data),
  });
  const holderNumbers = availBarcodes.holders.find(h => h.holder_key === issuedHolder)?.numbers || [];

  const { data: drivers = [] } = useQuery({
    queryKey: ['drivers'],
    queryFn: () => api.get('/users/drivers').then(r => r.data),
  });

  // Default to Jaco (or first driver) once list loads
  useEffect(() => {
    if (drivers.length && !pickupDriverId) {
      const jaco = drivers.find(d => d.name.toLowerCase().includes('jaco'));
      setPickupDriverId((jaco || drivers[0]).id);
    }
  }, [drivers]);

  // A sticker can't be both the pickup's own number and one of its extra boxes
  useEffect(() => {
    if (!trackingInput) return;
    setExtraBoxes(b => b.filter(c => c !== trackingInput.toUpperCase()));
  }, [trackingInput]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Who pays: RECEIVER by default; auto-switches to the sender when the sender
  // has a "pays for parcels they send" arrangement; always changeable below.
  const [payerRole, setPayerRole] = useState('recipient'); // 'recipient' | 'sender'
  useEffect(() => {
    setPayerRole(sender?.pays_for_sent ? 'sender' : 'recipient');
  }, [sender?.id, sender?.pays_for_sent]);
  const payer = payerRole === 'sender' ? sender : recipient;

  // Debt check on the payer. Warns — does not block — when they owe money.
  const { data: payerDebt } = useQuery({
    queryKey: ['debtor-check', payer?.id],
    queryFn: () => api.get(`/billing/debtors/${payer.id}`).then(r => r.data),
    enabled: !!payer?.id,
  });
  const owesMoney = payerDebt && parseFloat(payerDebt.balance) > 0;
  const owesOverdue = payerDebt && parseFloat(payerDebt.overdue) > 0;

  const checkTracking = async (val) => {
    const v = val.trim().toUpperCase();
    setTrackingInput(v);
    if (!v) { setTrackingOk(null); return; }
    try {
      const { data } = await api.get(`/parcels/check-tracking/${v}`);
      setTrackingOk(data.available);
    } catch { setTrackingOk(null); }
  };

  const generateRandom = () => {
    const hex = Math.random().toString(16).slice(2, 8).toUpperCase();
    const now = new Date();
    const y = String(now.getFullYear()).slice(-2);
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const val = `EC${y}${m}${hex}`;
    setTrackingInput(val);
    setTrackingOk(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!sender) return toast.error('Select a sender');
    if (!recipient) return toast.error('Select a receiver');
    if (manualTracking && !trackingInput) return toast.error('Enter a tracking number');
    if (manualTracking && trackingOk === false) return toast.error('That tracking number is already in use');

    setLoading(true);
    try {
      const payload = {
        sender_id: sender.id,
        recipient_id: recipient.id,
        ...form,
        weight: form.weight || null,
        length: form.length || null,
        width: form.width || null,
        height: form.height || null,
        manual_price: form.manual_price || null,
        price_tier_id: form.price_tier_id || null,
        pickup_driver_id: pickupDriverId || null,
        bill_to_client_id: payer?.id || null,
      };
      if (manualTracking && trackingInput) payload.tracking_number = trackingInput;
      if (extraBoxes.length) payload.extra_barcodes = extraBoxes;

      const { data } = await api.post('/parcels', payload);
      const boxes = (data.boxes_added?.length || 0) + 1;
      toast.success(boxes > 1
        ? `Parcel created: ${data.tracking_number} — ${boxes} boxes`
        : `Parcel created: ${data.tracking_number}`);
      if (extraBoxes.length && (data.boxes_added?.length || 0) < extraBoxes.length) {
        const missed = extraBoxes.filter(c => !(data.boxes_added || []).includes(c));
        toast.error(`Already in use, not added as boxes: ${missed.join(', ')}`);
      }
      navigate(`/parcels/${data.tracking_number}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create parcel');
    } finally {
      setLoading(false);
    }
  };

  const selectedTier = tiers.find(t => t.id === form.price_tier_id);

  // Volumetric pricing preview — charge on whichever is heavier:
  // actual weight or volumetric weight (L × W × H in cm ÷ 5000)
  const vw = parseFloat(form.weight) || 0;
  const vL = parseFloat(form.length) || 0;
  const vW = parseFloat(form.width)  || 0;
  const vH = parseFloat(form.height) || 0;
  const volumetric = (vL > 0 && vW > 0 && vH > 0) ? (vL * vW * vH) / 5000 : 0;
  const chargeable = Math.max(vw, volumetric);
  const suggestedTier = chargeable > 0
    ? [...tiers]
        .filter(t => t.max_weight == null || parseFloat(t.max_weight) >= chargeable)
        .sort((a, b) => (parseFloat(a.max_weight) || 1e9) - (parseFloat(b.max_weight) || 1e9))[0]
    : null;

  // Auto-select the matching tier as weight/dimensions are typed
  // (manually picking another tier sticks until the measurements change)
  useEffect(() => {
    if (suggestedTier) setForm(f => ({ ...f, price_tier_id: suggestedTier.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedTier?.id]);

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <h1 className="text-xl font-bold text-gray-800">New Parcel</h1>

      <form onSubmit={submit} className="space-y-5">

        {/* Sender / Recipient */}
        <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Parties</h2>
          <EntitySearch label="Sender" hint="who is sending the parcel" value={sender} onChange={setSender} />
          <div className="flex items-center gap-2 text-gray-300">
            <div className="flex-1 h-px bg-gray-100" />
            <ChevronRight size={16} />
            <div className="flex-1 h-px bg-gray-100" />
          </div>
          <EntitySearch label="Receiver" hint="who will receive the parcel" value={recipient} onChange={setRecipient} />

          {/* Who pays for this parcel */}
          {(sender || recipient) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Who pays for this parcel?</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setPayerRole('recipient')} disabled={!recipient}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-40 ${
                    payerRole === 'recipient' ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400'
                  }`}>
                  Receiver{recipient ? ` — ${recipient.name}` : ''} {payerRole === 'recipient' && '✓'}
                </button>
                <button type="button" onClick={() => setPayerRole('sender')} disabled={!sender}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-40 ${
                    payerRole === 'sender' ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400'
                  }`}>
                  Sender{sender ? ` — ${sender.name}` : ''} {payerRole === 'sender' && '✓'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                The receiver pays by default. This decides whose account the parcel is billed to.
              </p>
            </div>
          )}

          {/* Debt warning — the payer owes money */}
          {owesMoney && (
            <div className={`rounded-lg border-2 p-4 ${owesOverdue ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`}>
              <p className={`font-bold text-sm ${owesOverdue ? 'text-red-700' : 'text-amber-700'}`}>
                ⚠️ {payer.name} owes R{parseFloat(payerDebt.balance).toFixed(2)}
                {owesOverdue && <> — R{parseFloat(payerDebt.overdue).toFixed(2)} is OVERDUE
                  {payerDebt.oldest_overdue_days ? ` (oldest ${payerDebt.oldest_overdue_days} days)` : ''}</>}
              </p>
              <p className={`text-xs mt-1 ${owesOverdue ? 'text-red-600' : 'text-amber-600'}`}>
                This client pays for this parcel. Consider asking for payment before booking more work.{' '}
                <a href="/debt-collection" className="underline font-medium">Open Debt Collection</a>
              </p>
            </div>
          )}

          {drivers.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Truck size={14} className="text-gray-400" /> Pickup Driver
              </label>
              <select
                value={pickupDriverId}
                onChange={e => setPickupDriverId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              >
                <option value="">No driver assigned</option>
                {drivers.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Tracking number */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wide mb-3">Tracking Number</h2>
          <div className="flex gap-2 mb-3">
            {[
              ['auto', 'Auto-generate'],
              ['issued', `From barcode book${availBarcodes.total ? ` (${availBarcodes.total})` : ''}`],
              ['manual', 'Enter manually'],
            ].map(([mode, lbl]) => (
              <button key={mode} type="button"
                onClick={() => { setTrackingMode(mode); setTrackingInput(''); setTrackingOk(null); setIssuedHolder(''); }}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  trackingMode === mode ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400'
                }`}>
                {lbl}
              </button>
            ))}
          </div>

          {trackingMode === 'auto' && (
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2.5">
              <Zap size={15} className="text-orange-400" />
              Auto-generated on save (format: EC2605XXXXXX)
            </div>
          )}

          {trackingMode === 'issued' && (
            availBarcodes.total === 0 ? (
              <div className="text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2.5">
                No unused issued barcodes. Issue some on the <a href="/barcodes" className="text-orange-500 hover:underline">Barcodes</a> page first.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Holder</label>
                  <select value={issuedHolder}
                    onChange={e => { setIssuedHolder(e.target.value); setTrackingInput(''); setTrackingOk(null); }}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                    <option value="">— Choose holder —</option>
                    {availBarcodes.holders.map(h => (
                      <option key={h.holder_key} value={h.holder_key}>{h.holder_name} ({h.numbers.length})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tracking number</label>
                  <select value={trackingInput} disabled={!issuedHolder}
                    onChange={e => { setTrackingInput(e.target.value); setTrackingOk(!!e.target.value); }}
                    className="w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-gray-50">
                    <option value="">{issuedHolder ? '— Choose number —' : 'Pick a holder first'}</option>
                    {holderNumbers.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            )
          )}

          {trackingMode === 'manual' && (
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={trackingInput}
                  onChange={e => checkTracking(e.target.value)}
                  placeholder="e.g. EC2605ABC123"
                  maxLength={20}
                  className={`w-full border rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 ${
                    trackingOk === true ? 'border-green-400 focus:ring-green-400' :
                    trackingOk === false ? 'border-red-400 focus:ring-red-400' :
                    'focus:ring-orange-400'
                  }`}
                />
                {trackingOk === true && <p className="text-xs text-green-600 mt-1">Available</p>}
                {trackingOk === false && <p className="text-xs text-red-500 mt-1">Already in use</p>}
              </div>
              <button type="button" onClick={generateRandom}
                className="flex items-center gap-1 border rounded-lg px-3 py-2 text-xs text-gray-600 hover:bg-gray-50">
                <RefreshCw size={13} /> Random
              </button>
            </div>
          )}
        </div>

        {/* Boxes on this pickup */}
        <NewParcelBoxes
          avail={availBarcodes}
          mainNumber={trackingInput}
          chosen={extraBoxes}
          setChosen={setExtraBoxes}
        />

        {/* Parcel details */}
        <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Parcel Details</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input type="text" value={form.description} onChange={set('description')}
              placeholder="e.g. Furniture, Electronics, Clothing..."
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[['weight','Weight (kg)'],['length','Length (cm)'],['width','Width (cm)'],['height','Height (cm)']].map(([k,lbl]) => (
              <div key={k}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{lbl}</label>
                <input type="number" step="0.1" min="0" value={form[k]} onChange={set(k)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            ))}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={form.notes} onChange={set('notes')} rows={2}
              placeholder="Special handling, fragile, access instructions..."
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-white rounded-xl shadow-sm p-5 space-y-3">
          <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Pricing</h2>
          {chargeable > 0 && (
            <div className="bg-orange-50 border border-orange-100 rounded-lg px-4 py-3 text-sm text-gray-700">
              Scale: <strong>{vw.toFixed(1)} kg</strong>
              {volumetric > 0 && <> · Volumetric (L×W×H÷5000): <strong>{volumetric.toFixed(1)} kg</strong></>}
              {' '}→ charge on <strong>{chargeable.toFixed(1)} kg</strong>
              {suggestedTier
                ? <> → tier <strong>{suggestedTier.name}</strong> = <strong className="text-orange-700">R{parseFloat(suggestedTier.price).toFixed(2)}</strong></>
                : <span className="text-red-600"> — no tier covers this weight, enter a manual price</span>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price Tier</label>
              <select value={form.price_tier_id} onChange={set('price_tier_id')}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                <option value="">Select tier...</option>
                {tiers.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} — R{parseFloat(t.price).toFixed(2)} (up to {t.max_weight}kg)
                  </option>
                ))}
              </select>
              {selectedTier && !form.manual_price && (
                <p className="text-xs text-green-600 mt-1">Price: R{parseFloat(selectedTier.price).toFixed(2)}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Manual Price (R)</label>
              <input type="number" step="0.01" min="0" value={form.manual_price} onChange={set('manual_price')}
                placeholder="Overrides tier price"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>
        </div>

        {/* Delivery date */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <DatePicker value={form.scheduled_delivery} onChange={v => setForm(f => ({ ...f, scheduled_delivery: v }))} />
        </div>

        <button type="submit" disabled={loading}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50 text-base">
          {loading ? 'Creating parcel...' : 'Create Parcel & Generate Label'}
        </button>
      </form>
    </div>
  );
}
